import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixBookingsStaffSettingsV1StaffMemberSettings from './bookings-staff-settings-v1-staff-member-settings-staff-member-settings.http.js';

/**
 * Settings that control the visibility of staff members' contact information.
 *
 * These settings determine whether the `email` and `phone` fields are populated in [staff member objects](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/staff-member-object).
 * When enabled, these fields contain staff contact information.
 * When disabled, these fields are empty if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions.
 * Identities with appropriate permissions may still access the information.
 *
 * This is a singleton resource. Each site has exactly one settings object,
 * automatically created when Wix Bookings is installed.
 */
export interface StaffMemberSettings {
  /**
   * ID of the staff member settings object. There is one settings object per site.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the `staffMemberSettings` object is updated.
   * To prevent conflicting changes, the current revision must be specified when updating the `staffMemberSettings` object.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the `staffMemberSettings` object was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the `staffMemberSettings` object was last updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _updatedDate?: Date | null;
  /** Privacy settings for controlling staff member contact information visibility in API responses and on the live site. */
  contactInformation?: ContactInformation;
  /**
   * Privacy settings for controlling staff member properties visibility, such as gender, in API responses and on the live site.
   * @internal
   */
  staffProperties?: StaffProperties;
  /**
   * Custom field data for the staff member settings.
   * Extended fields must be configured in the app dashboard before they can be accessed with API calls.
   *
   * Learn more about extended fields at https://dev.wix.com/docs/rest/articles/getting-started/extended-fields.
   */
  extendedFields?: ExtendedFields;
}

/**
 * Privacy settings for staff member contact information.
 *
 * These settings control whether staff members' email addresses and phone numbers
 * are populated in Staff Member objects returned by the Staff Members API.
 */
export interface ContactInformation {
  /**
   * Whether staff members' email addresses and phone numbers are accessible in API responses and on the live site.
   *
   * - `true`: The `email` and `phone` fields in [staff member objects](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/staff-member-object) contain actual contact information, and this information is displayed on the live site.
   * - `false`: These fields are empty in API responses if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions, and contact information isn't displayed on the live site.
   *
   * Identities with appropriate permissions can still access this information in API responses regardless of this setting.
   *
   * Default: `false`
   */
  publiclyAccessible?: boolean | null;
}

/**
 * Privacy settings for staff member properties.
 *
 * These settings control whether staff members' properties, such as gender,
 * are populated in [staff member objects](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/staff-member-object).
 * Each property has its own visibility setting.
 */
export interface StaffProperties {
  /**
   * Whether staff members' gender is accessible in API responses and on the live site.
   *
   * - `true`: The `gender` field in [staff member objects](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/staff-member-object) contains the staff member's gender, and this information is displayed on the live site.
   * - `false`: The field returns `UNKNOWN_GENDER` in API responses if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions, and gender isn't displayed on the live site.
   *
   * Identities with appropriate permissions can still access this information in API responses regardless of this setting.
   *
   * Default: `false`
   */
  genderPubliclyAccessible?: boolean | null;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface GetStaffMemberSettingsRequest {}

export interface GetStaffMemberSettingsResponse {
  /** Retrieved staff member settings. */
  staffMemberSettings?: StaffMemberSettings;
}

export interface UpdateStaffMemberSettingsRequest {
  /** Staff member settings to update, may be partial. */
  staffMemberSettings: StaffMemberSettings;
  /**
   * Set of fields to update.
   *
   * Fields not included in `fieldMask.paths` are ignored.
   * @internal
   */
  fieldMask?: string[];
}

export interface UpdateStaffMemberSettingsResponse {
  /** Updated staff member settings. */
  staffMemberSettings?: StaffMemberSettings;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface StaffMemberSettingsUpdatedEnvelope {
  entity: StaffMemberSettings;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when a `staffMemberSettings` object is updated.
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionId STAFF_MEMBERS_SETTINGS.READ
 * @webhook
 * @eventType wix.bookings.staff_settings.v1.staff_member_settings_updated
 * @slug updated
 * @documentationMaturity preview
 */
export declare function onStaffMemberSettingsUpdated(
  handler: (event: StaffMemberSettingsUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Retrieves the current privacy settings for staff member contact information.
 *
 * Use this method to check whether the `email` and `phone` fields in [staff member objects](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/staff-member-object) are accessible in API responses and on the live site.
 * @public
 * @documentationMaturity preview
 * @permissionId STAFF_MEMBERS_SETTINGS.READ
 * @applicableIdentity APP
 * @returns Retrieved staff member settings.
 * @fqn wix.bookings.staff.settings.v1.StaffMemberSettingsService.GetStaffMemberSettings
 */
export async function getStaffMemberSettings(): Promise<StaffMemberSettings> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts =
    ambassadorWixBookingsStaffSettingsV1StaffMemberSettings.getStaffMemberSettings(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.staffMemberSettings!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates privacy settings for staff member contact information.
 *
 * Each time the settings are updated, `revision` increments by 1.
 * The current `revision` must be passed when updating the settings.
 * This ensures you're working with the latest settings and prevents unintended overwrites.
 *
 * Changes to these settings take effect immediately and affect both API responses and live site display.
 * When you enable `contactInformation.publiclyAccessible`, the `email` and `phone` fields in [staff member objects](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/staff-member-object) include actual contact information, and this information is displayed on the live site.
 * When you disable it, these fields are empty in API responses if the calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) doesn't have sufficient permissions, and contact information isn't displayed on the live site.
 *
 * Identities with appropriate permissions can access staff contact information in API responses regardless of this setting.
 * @param staffMemberSettings - Staff member settings to update, may be partial.
 * @public
 * @documentationMaturity preview
 * @requiredField staffMemberSettings
 * @requiredField staffMemberSettings.revision
 * @permissionId STAFF_MEMBERS_SETTINGS.UPDATE
 * @applicableIdentity APP
 * @returns Updated staff member settings.
 * @fqn wix.bookings.staff.settings.v1.StaffMemberSettingsService.UpdateStaffMemberSettings
 */
export async function updateStaffMemberSettings(
  staffMemberSettings: NonNullablePaths<StaffMemberSettings, `revision`, 2>
): Promise<StaffMemberSettings> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    staffMemberSettings: staffMemberSettings,
  });

  const reqOpts =
    ambassadorWixBookingsStaffSettingsV1StaffMemberSettings.updateStaffMemberSettings(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)
      ?.staffMemberSettings!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { staffMemberSettings: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['staffMemberSettings']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}
